<template>
  <div class="module-map">

    <l-map
      ref="map"
      :zoom="defaultZoom"
      :center="mapCenter"
      :options="mapOptions"
      @ready="onMapReady"
    >
      <l-tile-layer
        :url="tileUrl"
        :attribution="tileAttribution"
        :options="tileOptions"
      />
    </l-map>

    <!-- LEFT: category filter strip -->
    <div class="fstrip" role="group" aria-label="Kategorie-Filter">
      <button
        v-for="key in bucketKeys"
        :key="key"
        class="icob"
        :class="{ active: categories[key] }"
        type="button"
        :title="labels[key]"
        :aria-pressed="categories[key] ? 'true' : 'false'"
        @click="toggleCategory(key)"
      >
        <span class="icob-ico" :style="maskStyle(buckets[key].glyph)"></span>
        <span class="icob-dot" :style="{ background: buckets[key].color }"></span>
      </button>
    </div>

    <!-- RIGHT: locate + zoom FABs -->
    <div class="fabs">
      <button class="fab fab--locate" type="button" title="Mein Standort" @click="locate">
        <span class="fab-ico" :style="maskStyle(icons.standort)"></span>
      </button>
      <button class="fab" type="button" title="Vergrößern" @click="zoomIn">
        <span class="fab-ico" :style="maskStyle(icons.zoomPlus)"></span>
      </button>
      <button class="fab" type="button" title="Verkleinern" @click="zoomOut">
        <span class="fab-ico" :style="maskStyle(icons.zoomMinus)"></span>
      </button>
    </div>

    <!-- Geolocation failure feedback (auto-hides) -->
    <div v-if="locateError" class="locate-toast" role="status">{{ locateError }}</div>

  </div>
</template>

<script>

  import L from 'leaflet'
  import debounce from 'lodash/debounce'
  import { LMap, LTileLayer } from 'vue2-leaflet'

  // --- Static config -------------------------------------------------------

  // Defaults are hardcoded for now (Salzkammergut / Wolfgangsee region).
  // Later these can be read from module.resource.content.
  const DEFAULT_CENTER = [47.735, 13.40]
  const DEFAULT_ZOOM   = 13
  const MIN_FETCH_ZOOM = 11   // below this the viewport is too large to fetch
  const LOCATE_ZOOM    = 17   // upper bound when centring on the user's position
  const ACCURACY_CIRCLE_MIN = 50  // metres; below this the fix is good enough to
                                  // skip the accuracy circle entirely
  const FETCH_LIMIT    = 100  // API max per request
  const LANG           = 'de'
  const DEBOUNCE_MS    = 400

  // Each kiosk category maps to one tourbodata data type. `apiCategories`
  // narrows a shared data type (poi) to a subset of categories; null means
  // "fetch the whole data type".
  const CATEGORY_BUCKETS = {
    event: {
      endpoint: 'events',
      apiCategories: null,
      color: '#F7B400',
      marker: require('@/assets/map/marker-event.svg'),
      glyph: require('@/assets/map/kalender.svg'),
      btnActive: require('@/assets/map/btn-aktiv-event.svg'),
      btnInactive: require('@/assets/map/btn-inaktiv-event.svg'),
    },
    unterkunft: {
      endpoint: 'accommodation',
      apiCategories: null,
      color: '#0A8FA5',
      marker: require('@/assets/map/marker-unterkunft.svg'),
      glyph: require('@/assets/map/schlafen.svg'),
      btnActive: require('@/assets/map/btn-aktiv-unterkunft.svg'),
      btnInactive: require('@/assets/map/btn-inaktiv-unterkunft.svg'),
    },
    restaurant: {
      endpoint: 'gastronomy',
      apiCategories: null,
      color: '#E8562A',
      marker: require('@/assets/map/marker-restaurant.svg'),
      glyph: require('@/assets/map/restaurant.svg'),
      btnActive: require('@/assets/map/btn-aktiv-restaurant.svg'),
      btnInactive: require('@/assets/map/btn-inaktiv-restaurant.svg'),
    },
    arzt: {
      endpoint: 'poi',
      apiCategories: ['Arzt/Zahnarzt', 'Apotheke'],
      color: '#DC2626',
      marker: require('@/assets/map/marker-arzt.svg'),
      glyph: require('@/assets/map/arzt.svg'),
      btnActive: require('@/assets/map/btn-aktiv-arzt.svg'),
      btnInactive: require('@/assets/map/btn-inaktiv-arzt.svg'),
    },
    einkaufen: {
      endpoint: 'poi',
      apiCategories: ['Bäckerei/Konditorei/Lebzelterei', 'Bankomat', 'Einkaufen'],
      color: '#7C3AED',
      marker: require('@/assets/map/marker-einkaufen.svg'),
      glyph: require('@/assets/map/einkaufen.svg'),
      btnActive: require('@/assets/map/btn-aktiv-einkaufen.svg'),
      btnInactive: require('@/assets/map/btn-inaktiv-einkaufen.svg'),
    },
  }

  // The config editor may deliver a custom map centre (lat/lng). Both fields are
  // optional and can arrive as strings, so coerce and sanity-check them; a
  // half-filled or out-of-range pair falls back to the hardcoded default.
  function parseCoords(content) {
    if (!content) return null
    const lat = Number(content.lat)
    const lng = Number(content.lng)
    if (content.lat == null || content.lat === '' || !isFinite(lat)) return null
    if (content.lng == null || content.lng === '' || !isFinite(lng)) return null
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null
    return [lat, lng]
  }

  // Tooltips / accessible labels for the category strip.
  const LABELS = {
    event: 'Veranstaltungen',
    unterkunft: 'Unterkunft',
    restaurant: 'Restaurants',
    arzt: 'Arzt & Apotheke',
    einkaufen: 'Einkaufen',
  }

  // Marker SVGs use a 40x56 teardrop pin. Sized up from the original 30x42 so
  // the head is a comfortable touch target on kiosk/touch screens (clustering
  // keeps a dense viewport readable); anchor at the bottom tip.
  const ICON_SIZE = [52, 73]
  const ICON_ANCHOR = [ICON_SIZE[0] / 2, ICON_SIZE[1] ]
  // Glyph sits in the round head. In the 40x56 source SVG the head is a circle
  // of r=20 centred at (20, 20), so centre the glyph on 20/56 of the height
  // rather than offsetting it from the top.
  const GLYPH_SIZE = Math.round(ICON_SIZE[0] * 0.45)
  const GLYPH_CENTER_Y = Math.round(ICON_SIZE[1] * (20 / 56))
  // Nudge left to optically centre the glyph in the head.
  const GLYPH_CENTER_X = ICON_SIZE[0] / 2 - 1

  // Compose a marker = colored teardrop pin + category glyph (tinted white)
  // centered in the pin head. Inline styles keep it self-contained (Leaflet
  // creates this node outside Vue's scoped styling).
  function makeMarkerIcon(bucket) {
    return L.divIcon({
      className: 'tb-pin',
      html:
        `<div style="position:relative;width:${ICON_SIZE[0]}px;height:${ICON_SIZE[1]}px;">` +
          `<img src="${bucket.marker}" ` +
            `style="position:absolute;top:0;left:0;width:${ICON_SIZE[0]}px;height:${ICON_SIZE[1]}px;display:block;" />` +
          `<img src="${bucket.glyph}" ` +
            `style="position:absolute;left:${GLYPH_CENTER_X}px;top:${GLYPH_CENTER_Y}px;width:${GLYPH_SIZE}px;height:${GLYPH_SIZE}px;` +
            `transform:translate(-50%,-50%);object-fit:contain;filter:brightness(0) invert(1);" />` +
        `</div>`,
      iconSize: ICON_SIZE,
      iconAnchor: ICON_ANCHOR,
      // Float the popup above the pin head instead of over the anchor tip.
      popupAnchor: [0, -ICON_SIZE[1] + 4],
    })
  }

  // --- Popup content -------------------------------------------------------

  // Most text fields come back as [{ lang: 'de-DE', value: '…' }]. Prefer the
  // requested language, fall back to whatever the API sent first.
  function pickLocalized(field) {
    if (typeof field === 'string') return field
    if (!Array.isArray(field) || !field.length) return ''
    const hit = field.find(
      (v) => v && typeof v.lang === 'string' && v.lang.toLowerCase().indexOf(LANG) === 0
    )
    return ((hit || field[0]) || {}).value || ''
  }

  // Teasers contain markup (<br> mostly). Flatten to plain text so the popup
  // can't inherit stray tags from the feed.
  function toPlainText(html) {
    return String(html || '')
      .replace(/<br\s*\/?>/gi, ' ')
      .replace(/<\/p>/gi, ' ')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/gi, ' ')
      .replace(/&amp;/gi, '&')
      .replace(/&quot;/gi, '"')
      .replace(/&#0?39;|&apos;/gi, "'")
      .replace(/&lt;/gi, '<')
      .replace(/&gt;/gi, '>')
      .replace(/\s+/g, ' ')
      .trim()
  }

  function escapeHtml(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  // The feed supplies every website with an http(s) scheme, but it is still
  // third-party data landing in an href: anything that isn't http(s) (a
  // `javascript:` URL above all) is dropped rather than rendered.
  function safeUrl(raw) {
    const url = String(raw || '').trim()
    if (!url) return ''
    return /^https?:\/\/[^\s]+$/i.test(url) ? url : ''
  }

  // Kiosk popups are narrow, so show the host rather than the full path — the
  // href keeps the deep link, the label just says where the tap leads.
  const LINK_MAX = 34

  function displayUrl(url) {
    const bare = url.replace(/^https?:\/\//i, '').replace(/\/+$/, '')
    return bare.length > LINK_MAX ? bare.slice(0, LINK_MAX - 1) + '…' : bare
  }

  const TEASER_MAX = 140

  function truncate(s, max) {
    return s.length > max ? s.slice(0, max - 1).replace(/\s+\S*$/, '') + '…' : s
  }

  // Events carry no teaser; their next date is the more useful subtitle.
  function formatEventDate(item) {
    if (!item.nextDate) return ''
    const d = new Date(item.nextDate)
    if (isNaN(d.getTime())) return ''
    let out = d.toLocaleString('de-AT', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
    if (item.totalDates > 1) out += ` · ${item.totalDates} Termine`
    if (item.isSoldOut) out += ' · Ausverkauft'
    return out
  }

  // Title + one context line + address + whatever contact data the item has.
  // Returns '' when there is nothing worth showing, so such markers stay
  // popup-less. Every contact field is optional — the feed fills them in
  // unevenly (roughly four items in five carry a phone number, events carry
  // none at all), so each row is emitted only when its value is present.
  function makePopupHtml(item) {
    const title = toPlainText(pickLocalized(item.name))
    if (!title) return ''
    const sub =
      formatEventDate(item) ||
      truncate(toPlainText(pickLocalized(item.teaser)), TEASER_MAX)
    const address = toPlainText(item.address)
    const phone = toPlainText(item.phone)
    const email = toPlainText(item.email)
    const website = safeUrl(item.website)

    // Phone and email are text, not tel:/mailto: links: these run on
    // wall-mounted kiosks with no dialer and no mail client, so either link
    // would dead-end on a tap. The website does open — in a new tab, keeping
    // the map underneath it.
    const contact =
      (phone ? `<div class="tb-pop-ctline">Tel. ${escapeHtml(phone)}</div>` : '') +
      (email ? `<div class="tb-pop-ctline">${escapeHtml(email)}</div>` : '') +
      (website
        ? `<a class="tb-pop-link" href="${escapeHtml(website)}"` +
          ` target="_blank" rel="noopener noreferrer">` +
          `${escapeHtml(displayUrl(website))}</a>`
        : '')

    return (
      `<div class="tb-pop">` +
        `<div class="tb-pop-title">${escapeHtml(title)}</div>` +
        (sub ? `<div class="tb-pop-sub">${escapeHtml(sub)}</div>` : '') +
        (address ? `<div class="tb-pop-addr">${escapeHtml(address)}</div>` : '') +
        (contact ? `<div class="tb-pop-contact">${contact}</div>` : '') +
      `</div>`
    )
  }

  // "You are here" dot: a plain blue disc with a white ring, sized to stay
  // visible next to the (much larger) category pins without competing with them.
  const USER_DOT_SIZE = 20

  function makeUserIcon() {
    return L.divIcon({
      className: 'tb-userdot',
      html:
        `<div style="width:${USER_DOT_SIZE}px;height:${USER_DOT_SIZE}px;border-radius:50%;` +
        `background:#1a73e8;border:3px solid #fff;box-sizing:border-box;` +
        `box-shadow:0 1px 4px rgba(0,0,0,.4);"></div>`,
      iconSize: [USER_DOT_SIZE, USER_DOT_SIZE],
      iconAnchor: [USER_DOT_SIZE / 2, USER_DOT_SIZE / 2],
    })
  }

  // One cluster group per category, colored to match the category. Nearby
  // markers collapse into a count bubble that splits apart on zoom-in.
  function makeClusterGroup(bucket) {
    return L.markerClusterGroup({
      showCoverageOnHover: false,
      spiderfyOnMaxZoom: true,
      maxClusterRadius: 75,
      iconCreateFunction(cluster) {
        const count = cluster.getChildCount()
        const size = count < 10 ? 52 : count < 100 ? 60 : 68
        return L.divIcon({
          className: 'tb-cluster',
          html:
            `<div style="width:${size}px;height:${size}px;line-height:${size}px;` +
            `border-radius:50%;background:${bucket.color};color:#fff;text-align:center;` +
            `font-weight:700;font-size:18px;border:2px solid #fff;` +
            `box-shadow:0 1px 4px rgba(0,0,0,.4);">${count}</div>`,
          iconSize: [size, size],
        })
      },
    })
  }

  export default {
    props: ['module'],
    components: { LMap, LTileLayer },
    data() {
      return {
        defaultCenter: DEFAULT_CENTER,
        defaultZoom: DEFAULT_ZOOM,
        // OSM's standard tiles: no API key, no account. (CARTO's Voyager tiles
        // were used here until they closed off keyless access and started
        // stamping "API KEY REQUIRED" onto every tile.)
        tileUrl: 'https://tile.openstreetmap.org/{z}/{x}/{y}.png',
        tileAttribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
        // No {s} subdomains: the OSM tile CDN serves everything from one host
        // over HTTP/2 and asks clients not to fan out across a/b/c any more.
        tileOptions: { maxZoom: 19 },
        mapOptions: {
          zoomControl: false,
          doubleClickZoom: false,
          attributionControl: true,
        },
        // Reactive selected-state per category (drives the toggle button art).
        // Default: nothing selected. When nothing is selected, ALL categories
        // are shown; selecting one or more narrows the map to just those.
        categories: Object.keys(CATEGORY_BUCKETS).reduce((acc, k) => {
          acc[k] = false
          return acc
        }, {}),
        // Transient message shown when geolocation is denied/unavailable.
        locateError: null,
        icons: {
          zoomPlus: require('@/assets/map/zoom-plus.svg'),
          zoomMinus: require('@/assets/map/zoom-minus.svg'),
          standort: require('@/assets/map/standort-fab.svg'),
        },
      }
    },
    computed: {
      bucketKeys() {
        return Object.keys(CATEGORY_BUCKETS)
      },
      buckets() {
        return CATEGORY_BUCKETS
      },
      labels() {
        return LABELS
      },
      // Optional per-module config coming from the config editor.
      content() {
        return (this.module && this.module.resource && this.module.resource.content) || null
      },
      // Custom centre from the config, or null when unset/incomplete.
      customCoords() {
        return parseCoords(this.content)
      },
      mapCenter() {
        return this.customCoords || DEFAULT_CENTER
      },
      // Only honour the flag when there are usable coords to point at —
      // otherwise the locate button falls back to real geolocation.
      useCustomLocation() {
        if (!this.content || !this.customCoords) return false
        const flag = this.content.useCustomLocationMarker
        // The editor may serialize the checkbox as a string.
        return flag === true || flag === 'true' || flag === 1 || flag === '1'
      },
      noneSelected() {
        return Object.keys(this.categories).every((k) => !this.categories[k])
      },
      // Buckets to actually display: everything when nothing is selected,
      // otherwise only the selected ones.
      activeBuckets() {
        return Object.keys(CATEGORY_BUCKETS).filter(
          (k) => this.noneSelected || this.categories[k]
        )
      },
    },
    created() {
      // Non-reactive runtime state.
      this.map = null
      this.layerGroups = {}      // bucket key -> L.layerGroup
      this.leafletIcons = {}     // bucket key -> L.icon
      this.cancelSource = null   // axios CancelToken source for the active cycle
      this.userMarker = null     // "you are here" dot, created on first fix
      this.userCircle = null     // accuracy circle around the dot
      this.locateErrorTimer = null
      this.popupOpen = false     // suspends refetching while a popup is open
      this.refreshPending = false
      this.resumeTimer = null
      this.debouncedRefresh = debounce(this.refreshMarkers, DEBOUNCE_MS)
    },
    beforeDestroy() {
      if (this.debouncedRefresh) this.debouncedRefresh.cancel()
      if (this.cancelSource) this.cancelSource.cancel()
      if (this.locateErrorTimer) clearTimeout(this.locateErrorTimer)
      if (this.resumeTimer) clearTimeout(this.resumeTimer)
      if (this.map) {
        this.map.stopLocate()
        this.map.off('moveend', this.debouncedRefresh)
        this.map.off('locationfound', this.onLocationFound)
        this.map.off('locationerror', this.onLocationError)
        this.map.off('popupopen', this.onPopupOpen)
        this.map.off('popupclose', this.onPopupClose)
      }
    },
    methods: {
      onMapReady(map) {
        this.map = map

        // Build one layer group + icon per bucket; add enabled ones.
        const active = this.activeBuckets
        Object.keys(CATEGORY_BUCKETS).forEach((key) => {
          this.leafletIcons[key] = makeMarkerIcon(CATEGORY_BUCKETS[key])
          const group = makeClusterGroup(CATEGORY_BUCKETS[key])
          this.layerGroups[key] = group
          if (active.includes(key)) group.addTo(map)
        })

        map.on('moveend', this.debouncedRefresh)
        map.on('locationfound', this.onLocationFound)
        map.on('locationerror', this.onLocationError)
        map.on('popupopen', this.onPopupOpen)
        map.on('popupclose', this.onPopupClose)
        this.refreshMarkers()
      },

      onPopupOpen() {
        this.popupOpen = true
      },

      onPopupClose() {
        this.popupOpen = false
        if (!this.refreshPending) return
        // Tapping pin B closes A's popup before opening B's, so a refresh fired
        // straight from here would run "between" the two. Wait a tick and let
        // refreshMarkers re-check whether a new popup took A's place.
        if (this.resumeTimer) clearTimeout(this.resumeTimer)
        this.resumeTimer = setTimeout(() => {
          this.resumeTimer = null
          if (this.popupOpen || !this.refreshPending) return
          this.refreshPending = false
          this.refreshMarkers()
        }, 0)
      },

      onLocationFound(e) {
        const accuracy = e.accuracy || 0

        // The accuracy circle only earns its clutter when the fix is vague
        // (WiFi/IP-based); on a tight GPS fix the dot alone says it better.
        if (accuracy > ACCURACY_CIRCLE_MIN) {
          if (this.userCircle) {
            this.userCircle.setLatLng(e.latlng).setRadius(accuracy)
          } else {
            // Added before the dot so the dot draws on top of it.
            this.userCircle = L.circle(e.latlng, {
              radius: accuracy,
              color: '#1a73e8',
              weight: 1,
              opacity: 0.5,
              fillColor: '#1a73e8',
              fillOpacity: 0.12,
              interactive: false,
            }).addTo(this.map)
          }
        } else if (this.userCircle) {
          this.map.removeLayer(this.userCircle)
          this.userCircle = null
        }

        if (this.userMarker) {
          this.userMarker.setLatLng(e.latlng)
        } else {
          this.userMarker = L.marker(e.latlng, {
            icon: makeUserIcon(),
            interactive: false,
            keyboard: false,
            zIndexOffset: 1000,
          }).addTo(this.map)
        }
      },

      onLocationError(e) {
        this.showLocateError(
          e && e.code === 1
            ? 'Standortzugriff wurde abgelehnt.'
            : 'Standort konnte nicht ermittelt werden.'
        )
      },

      showLocateError(msg) {
        this.locateError = msg
        if (this.locateErrorTimer) clearTimeout(this.locateErrorTimer)
        this.locateErrorTimer = setTimeout(() => {
          this.locateError = null
        }, 4000)
      },

      refreshMarkers() {
        if (!this.map) return
        if (this.map.getZoom() < MIN_FETCH_ZOOM) return
        // Rebuilding a layer clears its markers, which would tear the open
        // popup off the map. Opening one can auto-pan (=> moveend => refresh),
        // so defer until it closes.
        if (this.popupOpen) {
          this.refreshPending = true
          return
        }

        // Cancel any in-flight cycle, then start a fresh one.
        if (this.cancelSource) this.cancelSource.cancel()
        this.cancelSource = this.$axios.CancelToken.source()
        const token = this.cancelSource.token

        this.activeBuckets.forEach((key) => this.fetchBucket(key, token))
      },

      fetchBucket(key, cancelToken) {
        const bucket = CATEGORY_BUCKETS[key]
        const b = this.map.getBounds()
        const params = {
          swLat: b.getSouth(),
          swLng: b.getWest(),
          neLat: b.getNorth(),
          neLng: b.getEast(),
          limit: FETCH_LIMIT,
          lang: LANG,
        }
        if (bucket.apiCategories) params.categories = bucket.apiCategories.join(',')

        this.$axios
          .get(`/tourbodata/${bucket.endpoint}`, { params, cancelToken })
          .then((r) => this.rebuildLayer(key, (r.data && r.data.data) || []))
          .catch((e) => {
            if (!this.$axios.isCancel(e)) {
              // Network/proxy errors are non-fatal: leave existing markers as-is.
            }
          })
      },

      rebuildLayer(key, items) {
        const group = this.layerGroups[key]
        if (!group) return
        // A response can land after a popup was opened (the request may even
        // predate it). Clearing now would destroy the marker the popup hangs
        // off, so drop this batch and refetch once the popup closes.
        if (this.popupOpen) {
          this.refreshPending = true
          return
        }
        group.clearLayers()
        const icon = this.leafletIcons[key]
        items.forEach((it) => {
          if (it.latitude == null || it.longitude == null) return
          const marker = L.marker([it.latitude, it.longitude], { icon })
          const html = makePopupHtml(it)
          if (html) {
            marker.bindPopup(html, {
              maxWidth: 280,
              minWidth: 180,
              autoPanPadding: [24, 24],
              closeButton: true,
            })
          }
          marker.addTo(group)
        })
      },

      toggleCategory(key) {
        this.$set(this.categories, key, !this.categories[key])
        // Toggling one category can flip the effective visibility of all of
        // them (none-selected shows everything), so re-sync the whole set.
        this.syncLayers()
      },

      syncLayers() {
        if (!this.map) return
        const active = this.activeBuckets
        Object.keys(CATEGORY_BUCKETS).forEach((key) => {
          const group = this.layerGroups[key]
          if (!group) return
          if (active.includes(key)) {
            if (!this.map.hasLayer(group)) group.addTo(this.map)
          } else if (this.map.hasLayer(group)) {
            this.map.removeLayer(group)
          }
        })
        // Make sure newly-shown buckets have data for the current viewport.
        this.refreshMarkers()
      },

      // Inline style that tints an SVG file to currentColor via CSS mask.
      maskStyle(url) {
        return {
          '-webkit-mask-image': 'url(' + url + ')',
          'mask-image': 'url(' + url + ')',
        }
      },

      zoomIn() {
        if (this.map) this.map.zoomIn()
      },
      zoomOut() {
        if (this.map) this.map.zoomOut()
      },
      locate() {
        if (!this.map) return
        // Kiosks sit at a fixed spot, so the module can pin "your location" to
        // the configured coords instead of asking the device where it is.
        if (this.useCustomLocation) {
          const latlng = L.latLng(this.customCoords)
          // accuracy 0 => exact fix, so no accuracy circle is drawn.
          this.onLocationFound({ latlng, accuracy: 0 })
          this.map.setView(latlng, Math.max(this.map.getZoom(), LOCATE_ZOOM))
          return
        }
        this.map.locate({ setView: true, maxZoom: LOCATE_ZOOM })
      },
    },
  }

</script>

<style scoped>

  .module-map {
    position: relative;
    width: 100%;
    height: 100%;
    min-height: 600px;
  }

  .module-map >>> .leaflet-container {
    width: 100%;
    height: 100%;
    min-height: 600px;
    /* Matches OSM's land colour so not-yet-loaded tiles don't flash a
       contrasting block while panning. */
    background: #f2efe9;
  }

  /* Category filter strip (top-left), styled after the OIS Wolfgangsee map. */
  .fstrip {
    position: absolute;
    top: 16px;
    left: 16px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 6px;
    border-radius: 16px;
    background: rgba(255, 255, 255, 0.93);
    -webkit-backdrop-filter: blur(16px);
    backdrop-filter: blur(16px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(0, 0, 0, 0.06);
  }

  .icob {
    position: relative;
    width: 56px;
    height: 56px;
    padding: 0;
    border: none;
    background: none;
    border-radius: 12px;
    color: #4d6c78;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: background 0.18s, color 0.18s, transform 0.1s, box-shadow 0.18s;
  }

  .icob:active {
    transform: scale(0.86);
  }

  .icob-ico {
    width: 26px;
    height: 26px;
    background-color: currentColor;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
    transition: background-color 0.18s;
  }

  /* Inactive: small category-colored dot at the bottom. */
  .icob-dot {
    position: absolute;
    bottom: 6px;
    left: 50%;
    transform: translateX(-50%);
    width: 5px;
    height: 5px;
    border-radius: 50%;
    transition: opacity 0.18s;
  }

  /* Active (selected): yellow fill, white glyph, dot hidden. */
  .icob.active {
    background: #f7b400;
    color: #fff;
    box-shadow: 0 2px 10px rgba(247, 180, 0, 0.30);
  }

  .icob.active .icob-dot {
    opacity: 0;
  }

  /* Zoom + locate FABs (bottom-right), styled after the OIS Wolfgangsee map. */
  .fabs {
    position: absolute;
    right: 18px;
    bottom: 22px;
    z-index: 1000;
    display: flex;
    flex-direction: column;
    gap: 10px;
  }

  .fab {
    width: 52px;
    height: 52px;
    padding: 0;
    border: none;
    border-radius: 16px;
    color: #4d6c78;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(255, 255, 255, 0.93);
    -webkit-backdrop-filter: blur(10px);
    backdrop-filter: blur(10px);
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.10), 0 2px 6px rgba(0, 0, 0, 0.06),
      0 0 0 1px rgba(0, 0, 0, 0.06);
    -webkit-tap-highlight-color: transparent;
    touch-action: manipulation;
    transition: background 0.18s, transform 0.1s;
  }

  .fab:active {
    transform: scale(0.9);
  }

  .fab--locate {
    background: #f7b400;
    color: #fff;
    box-shadow: 0 4px 16px rgba(247, 180, 0, 0.30);
  }

  .fab--locate:active {
    background: #d49900;
  }

  .locate-toast {
    position: absolute;
    left: 50%;
    bottom: 22px;
    transform: translateX(-50%);
    z-index: 1000;
    max-width: 78%;
    padding: 10px 16px;
    border-radius: 12px;
    background: rgba(20, 30, 35, 0.92);
    color: #fff;
    font-size: 14px;
    text-align: center;
    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.25);
  }

  .fab-ico {
    width: 22px;
    height: 22px;
    background-color: currentColor;
    -webkit-mask-position: center;
    mask-position: center;
    -webkit-mask-size: contain;
    mask-size: contain;
    -webkit-mask-repeat: no-repeat;
    mask-repeat: no-repeat;
  }

</style>

<!-- Non-scoped: Leaflet creates marker nodes outside Vue's scoped styling. -->
<style>

  .leaflet-div-icon.tb-pin,
  .leaflet-div-icon.tb-userdot {
    background: transparent;
    border: none;
  }

  /* Marker popups: match the rounded, soft-shadow language of the FABs. */
  .leaflet-popup-content-wrapper {
    border-radius: 14px;
    box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18), 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  .leaflet-popup-content {
    margin: 14px 16px;
  }

  /* Bigger hit area for the close button on touch screens. */
  .leaflet-popup-close-button {
    width: 32px;
    height: 32px;
    font-size: 20px;
    line-height: 30px;
    padding: 0;
  }

  .tb-pop-title {
    font-size: 18px;
    font-weight: 700;
    line-height: 1.25;
    color: #1f2d33;
  }

  .tb-pop-sub {
    margin-top: 6px;
    font-size: 15px;
    line-height: 1.4;
    color: #4d6c78;
  }

  .tb-pop-addr {
    margin-top: 8px;
    font-size: 14px;
    line-height: 1.35;
    color: #8a9aa1;
  }

  /* Contact block: separated from the address by a hairline so the tappable
     link doesn't read as part of the address text above it. */
  .tb-pop-contact {
    margin-top: 10px;
    padding-top: 8px;
    border-top: 1px solid #e6ecee;
  }

  /* Phone / email: plain text rows. Addresses can outrun the popup width, so
     let them wrap mid-token rather than widening the card. */
  .tb-pop-ctline {
    font-size: 15px;
    line-height: 1.35;
    color: #4d6c78;
    overflow-wrap: anywhere;
  }

  /* Full-width row at the 44px touch minimum: on a kiosk the whole strip is
     the target, not just the glyph-sized text inside it. */
  .tb-pop-link {
    display: flex;
    align-items: center;
    min-height: 44px;
    margin-top: 4px;
    font-size: 15px;
    font-weight: 600;
    line-height: 1.3;
    color: #0a8fa5;
    text-decoration: underline;
    word-break: break-all;
    -webkit-tap-highlight-color: rgba(10, 143, 165, 0.18);
  }

  .tb-pop-link:active {
    color: #076f80;
  }

</style>
